import React, { useState, useEffect, useRef } from 'react';
import { useScoreboard } from './context/ScoreboardContext';

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

const PenaltyEntry = ({ penalty, onRemove, isGameRunning, team, isLiveView = false }) => {
  const [playerNumber, setPlayerNumber] = useState(penalty.playerNumber || '');
  const [isEditingTime, setIsEditingTime] = useState(false);
  const timeRef = useRef(penalty.timeInSeconds);
  const { homePenalties, awayPenalties, setHomePenalties, setAwayPenalties, updateTeamInferiorityTime } = useScoreboard();

  const updatePenalty = (updates) => {
    const penalties = team === 'home' ? homePenalties : awayPenalties;
    const setPenalties = team === 'home' ? setHomePenalties : setAwayPenalties;
    
    const updatedPenalties = penalties.map(p => 
      p.id === penalty.id ? { ...p, ...updates } : p
    );
    setPenalties(updatedPenalties);
    
    // If time was changed, calculate the difference and update inferiority time
    if (updates.timeInSeconds !== undefined) {
      const oldTime = penalty.timeInSeconds;
      const newTime = updates.timeInSeconds;
      const timeDifference = newTime - oldTime;
      
      console.log('[updatePenalty] Time changed:', {
        team,
        oldTime,
        newTime,
        timeDifference,
        penaltiesLength: penalties.length
      });
      
      // Always pass the difference to updateTeamInferiorityTime
      // It will handle it correctly for both 1 penalty and 2+ penalties
      updateTeamInferiorityTime(team, updatedPenalties, timeDifference);
    }
  };

  const handlePlayerNumberChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 99)) {
      setPlayerNumber(value);
      updatePenalty({ playerNumber: value });
    }
  };

  const handlePlayerNumberBlur = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 99)) {
      setPlayerNumber(value);
      updatePenalty({ playerNumber: value });
    } else {
      setPlayerNumber(penalty.playerNumber || '');
    }
  };

  const handleTimeClick = () => {
    if (!isGameRunning && !isLiveView) {
      setIsEditingTime(true);
    }
  };

  const handleTimeChange = (e) => {
    const value = e.target.value;
    const [minutes, seconds] = value.split(':').map(Number);
    if (!isNaN(minutes) && !isNaN(seconds) && minutes >= 0 && seconds >= 0 && seconds < 60) {
      const newSeconds = minutes * 60 + seconds;
      timeRef.current = newSeconds;
      updatePenalty({ timeInSeconds: newSeconds });
    }
  };

  const handleTimeBlur = () => {
    setIsEditingTime(false);
  };

  const handleTimeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  // Keep local state in sync with penalty object
  useEffect(() => {
    setPlayerNumber(penalty.playerNumber || '');
  }, [penalty.playerNumber]);

  return (
    <div className="flex items-center justify-center gap-[1vh]">
      {isLiveView ? (
        // LiveView Layout - Spielernummer und Zeit nebeneinander, rechts ausgerichtet
        <div className="flex items-center justify-end w-full leading-tight whitespace-nowrap">
          <span className="text-[9vh] font-bold text-white font-chivo leading-none">
            {playerNumber ? playerNumber : ''}
          </span>
          <span className="text-[9vh] font-bold text-white font-chivo mx-[0.5vh] leading-none">
          {playerNumber ? '-' : ''}
          </span>
          <span className="text-[9vh] font-bold text-white font-chivo leading-none">
            {formatTime(penalty.timeInSeconds)}
          </span>
        </div>
      ) : (
        // Normale Ansicht mit Eingabefeldern
        <>
          <div className="w-[10vh] h-[8vh] p-[1vh] bg-white rounded-lg text-center relative border border-gray-300">
            <input
              type="number"
              min="1"
              max="99"
              value={playerNumber}
              onChange={handlePlayerNumberChange}
              onBlur={handlePlayerNumberBlur}
              disabled={isGameRunning}
              placeholder="99"
              className="w-full h-full text-[4vh] font-bold text-center bg-transparent appearance-textfield [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
          <div
            className="w-[16vh] h-[8vh] p-[1vh] bg-white rounded-lg border border-gray-300 flex items-center text-center cursor-pointer"
            onClick={handleTimeClick}
          >
            {isEditingTime ? (
              <input
                type="text"
                defaultValue={formatTime(penalty.timeInSeconds)}
                onChange={handleTimeChange}
                onBlur={handleTimeBlur}
                onKeyDown={handleTimeKeyDown}
                className="w-full h-full text-[4vh] font-bold text-center bg-transparent"
                autoFocus
                pattern="[0-9]{2}:[0-9]{2}"
                placeholder="00:00"
              />
            ) : (
              <span className="text-[4vh] font-bold">
                {formatTime(penalty.timeInSeconds)}
              </span>
            )}
          </div>
          {!isGameRunning && (
            <button
              onClick={() => onRemove(penalty.id)}
              className="w-[8vh] h-[8vh] bg-amber-500 text-black text-[4vh] font-bold rounded-lg hover:bg-amber-600"
              data-testid={`remove-penalty-${team}`}
            >
              -
            </button>
          )}
        </>
      )}
    </div>
  );
};

const PersonalPenalties = ({ team = 'home', isLiveView = false }) => {
  const {
    isGameRunning,
    homePenalties,
    awayPenalties,
    setHomePenalties,
    setAwayPenalties,
    centralTick,
    updateTeamInferiorityTime
  } = useScoreboard();

  const penalties = team === 'home' ? homePenalties : awayPenalties;
  const setPenalties = team === 'home' ? setHomePenalties : setAwayPenalties;
  const nextId = useRef(1);
  const penaltyStartTimes = useRef({});

  useEffect(() => {
    const handleReset = () => {
      setPenalties([]);
      penaltyStartTimes.current = {};
    };
    window.addEventListener('resetAll', handleReset);
    return () => window.removeEventListener('resetAll', handleReset);
  }, [setPenalties]);

  const addPenalty = () => {
    if (penalties.length < 3) {
      const newPenalty = {
        id: nextId.current++,
        timeInSeconds: 120,
        playerNumber: ''
      };
      const newPenalties = [...penalties, newPenalty];
      console.log('[addPenalty] Adding new penalty:', {
        team,
        newPenaltiesLength: newPenalties.length,
        penaltyTime: 120
      });
      setPenalties(newPenalties);
      // Update team inferiority time with new penalty time (120 seconds)
      updateTeamInferiorityTime(team, newPenalties, 120);
    }
  };

  const removePenalty = (id) => {
    // Find the penalty being removed
    const penaltyToRemove = penalties.find(p => p.id === id);
    const penaltyIndex = penalties.findIndex(p => p.id === id);
    const newPenalties = penalties.filter(p => p.id !== id);
    
    setPenalties(newPenalties);
    delete penaltyStartTimes.current[id];
    
    // Calculate time to subtract based on which penalty is being removed
    let timeToSubtract = 0;
    
    if (penaltyToRemove && penalties.length >= 2) {
      if (penaltyIndex === 0) {
        // First penalty: subtract CURRENT time
        if (isGameRunning && penaltyStartTimes.current[id]) {
          const penaltyInfo = penaltyStartTimes.current[id];
          if (penaltyInfo.isActive) {
            const now = Date.now();
            const elapsedMs = now - penaltyInfo.startTime;
            const elapsedSeconds = Math.floor(elapsedMs / 1000);
            timeToSubtract = Math.max(0, penaltyInfo.initialTime - elapsedSeconds);
          } else {
            timeToSubtract = penaltyToRemove.timeInSeconds;
          }
        } else {
          timeToSubtract = penaltyToRemove.timeInSeconds;
        }
      } else {
        // Second or third penalty: subtract 120 seconds (2:00)
        timeToSubtract = 120;
      }
      
      console.log('[removePenalty] Removing penalty:', {
        team,
        penaltyIndex,
        timeToSubtract,
        remainingPenalties: newPenalties.length
      });
      
      updateTeamInferiorityTime(team, newPenalties, -timeToSubtract);
    } else {
      // If removing the last penalty, set to 0
      console.log('[removePenalty] Removing last penalty');
      updateTeamInferiorityTime(team, newPenalties, null);
    }
  };

  // Update penalties using centralTick
  useEffect(() => {
    if (isGameRunning) {
      setPenalties(prev => {
        // Separate penalties that need to be removed (reached 0) from those that need to be updated
        const updatedPenalties = [];
        
        prev.forEach(penalty => {
          // Initialize start time if not set
          if (!penaltyStartTimes.current[penalty.id]) {
            penaltyStartTimes.current[penalty.id] = {
              startTime: Date.now(),
              initialTime: penalty.timeInSeconds
            };
          }

          const penaltyData = penaltyStartTimes.current[penalty.id];
          const elapsedMs = Date.now() - penaltyData.startTime;
          const elapsedSeconds = Math.floor(elapsedMs / 1000);
          const newTime = Math.max(0, penaltyData.initialTime - elapsedSeconds);
          
          // Wenn die Zeit 0 ist oder wenn die Zeit 1 ist und seit der letzten Aktualisierung
          // mindestens eine Sekunde vergangen ist, entfernen wir die Penalty
          if (newTime === 0) {
            // Kurz 00:00 anzeigen und dann entfernen
            // Wir fügen die Penalty mit 0 Sekunden hinzu, damit sie kurz als 00:00 angezeigt wird
            if (penalty.timeInSeconds > 0) {
              updatedPenalties.push({
                ...penalty,
                timeInSeconds: 0
              });
              
              // Nach kurzer Zeit entfernen (vor dem nächsten Tick)
              setTimeout(() => {
                setPenalties(current => current.filter(p => p.id !== penalty.id));
              }, 10);
            }
            
            delete penaltyStartTimes.current[penalty.id];
          } else {
            // Update time
            updatedPenalties.push({
              ...penalty,
              timeInSeconds: newTime
            });
          }
        });

        return updatedPenalties;
      });
    } else {
      // When game stops, save current times as initial times for next start
      penalties.forEach(penalty => {
        if (penaltyStartTimes.current[penalty.id]) {
          delete penaltyStartTimes.current[penalty.id];
        }
      });
    }
  }, [isGameRunning, centralTick, setPenalties]);

  // Listen for penalty updates from other windows
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === (team === 'home' ? 'HOME_PENALTIES_UPDATE' : 'AWAY_PENALTIES_UPDATE')) {
        setPenalties(event.data.payload);
      }
    };

    const broadcastChannel = new BroadcastChannel('rink-hockey-scoreboard');
    broadcastChannel.addEventListener('message', handleMessage);
    return () => broadcastChannel.removeEventListener('message', handleMessage);
  }, [team, setPenalties]);

  return (
    <div className={`flex flex-col ${isLiveView ? 'h-full' : ''}`}>
      <div className={`flex flex-col ${isLiveView ? 'gap-[0vh] items-end' : 'gap-[1vh] items-center'}`}>
        {penalties
          .filter(penalty => !isLiveView || (isLiveView))
          .map(penalty => (
          <PenaltyEntry
            key={penalty.id}
            penalty={penalty}
            onRemove={removePenalty}
            isGameRunning={isGameRunning}
            team={team}
            isLiveView={isLiveView}
          />
        ))}
      </div>
      {!isLiveView && penalties.length < 3 && !isGameRunning && (
        <button
          onClick={addPenalty}
          className="mt-[2vh] px-[2vh] py-[1vh] bg-amber-500 text-black font-bold rounded-lg hover:bg-amber-600 text-[2vh]"
        >
          ADD PENALTY
        </button>
      )}
    </div>
  );
};

export default PersonalPenalties;
